import { dbgOn, dbg } from '../../utils/debugFlag'
// ============================================
// usePriceAlerts.js
// Server-backed price alerts. Backend (server_swaps.py) owns the state,
// persists to alerts.json, and runs cross detection against the firehose
// of every decoded swap. This hook is a thin client:
//   - sends add_alert / remove_alert over the existing data WS
//   - listens for alert_triggered / alerts_list / alert_added / alert_removed
//   - mirrors backend state into local React state for the UI
//
// Owner identity = wallet pubkey. The user's wallet must be set on the
// server (via the existing set_wallet WS action) before alerts can be
// added — backend rejects otherwise. Once set, alerts survive disconnect,
// page reload, and even server restart (alerts.json on disk).
//
// Long-press detection (450ms, 6px tolerance) and the menu/notifications
// UI are unchanged from the localStorage version. Only the storage and
// cross-detection layers moved server-side.
// ============================================
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

const LONG_PRESS_MS = 450
const LONG_PRESS_TOLERANCE_PX = 6

// Lazy AudioContext so we don't construct one before the user has
// interacted with the page (browsers block playback otherwise).
let _bellCtx = null
// Exported for the followed-wallet alerts (useFollowAlerts) so both share one bell.
export function playBell() {
  try {
    if (!_bellCtx) {
      const Ctx = window.AudioContext || window.webkitAudioContext
      if (!Ctx) return
      _bellCtx = new Ctx()
    }
    const ctx = _bellCtx
    if (ctx.state === 'suspended') ctx.resume()
    const t0 = ctx.currentTime
    // Two-tone bell: fundamental + perfect fifth above, both with fast
    // attack and ~1.2s exponential decay. Sounds like a ping/bell rather
    // than a flat beep.
    const tones = [880, 1320] // A5, E6
    for (const freq of tones) {
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()
      osc.type = 'sine'
      osc.frequency.value = freq
      gain.gain.setValueAtTime(0.0001, t0)
      gain.gain.exponentialRampToValueAtTime(0.18, t0 + 0.01)
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 1.2)
      osc.connect(gain).connect(ctx.destination)
      osc.start(t0)
      osc.stop(t0 + 1.25)
    }
  } catch {}
}

function formatMcap(v) {
  if (v == null || !Number.isFinite(v)) return '--'
  const abs = Math.abs(v)
  if (abs >= 1e9) return `${(v / 1e9).toFixed(2)}B`
  if (abs >= 1e6) return `${(v / 1e6).toFixed(2)}M`
  if (abs >= 1e3) return `${(v / 1e3).toFixed(1)}K`
  return v.toFixed(0)
}

export default function usePriceAlerts({
  currentToken,
  currentWallet,    // base58 pubkey — required to add/list alerts
  wsRef,            // ref to the data WebSocket (data.wsRef from useChartData)
  onSwitchToken,    // (mint) => void, called when user clicks a triggered toast
  tokenSymbolMap,   // optional: { [mint]: symbol } for nicer labels
  market = 'sol',   // 'sol' | 'hl' | 'bitunix' — which market this chart is on
  currentSymbol,    // futures symbol when market !== 'sol' (e.g. 'BTC' or 'BTCUSDC')
  status,           // connection status from useChartData — re-attaches the WS
                    // listener after reconnects (wsRef mutation alone doesn't
                    // re-render; status changes do)
}) {
  // Master alerts list mirrored from the server. { id, wallet, mint, mcap, direction, created_at }
  const [alerts, setAlerts] = useState([])
  // Active toasts pending dismissal/click. { id, mint, symbol, mcap, direction, threshold, ts }
  const [notifications, setNotifications] = useState([])
  // Menu state: { x, y, mcap, mint } | null
  const [menuState, setMenuState] = useState(null)

  // Track wallet changes so we know when to clear the local mirror.
  const lastWalletSeenRef = useRef(null)

  // ===== WS message listener =====
  // Attaches once per ws-instance change. WebSocket supports multiple
  // message listeners — useChartData's existing handler keeps running
  // alongside this one.
  useEffect(() => {
    const ws = wsRef?.current
    if (!ws) return
    const handler = (e) => {
      let msg
      try { msg = JSON.parse(e.data) } catch { return }
      if (!msg || !msg.type) return
      if (dbgOn() && msg.type.startsWith('alert')) dbg('[AlertDebug] ws recv', msg.type, msg)
      switch (msg.type) {
        case 'alerts_list': {
          // Server pushes this on set_wallet ack and on explicit list_alerts.
          // Replace local state wholesale — server is source of truth.
          setAlerts(Array.isArray(msg.alerts) ? msg.alerts : [])
          break
        }
        case 'alert_added': {
          if (msg.alert) setAlerts(prev => [...prev, msg.alert])
          break
        }
        case 'alert_removed': {
          if (msg.id) setAlerts(prev => prev.filter(a => a.id !== msg.id))
          break
        }
        case 'alert_triggered': {
          // Drop fired alert from local list (server already removed it)
          // and queue a toast.
          setAlerts(prev => prev.filter(a => a.id !== msg.id))
          const market = msg.market || 'sol'
          // Prefer the name stored on the alert (captured at add time), then
          // the live symbol map, then the address slice as a last resort. The
          // stored name lets the toast show e.g. "BONK" even when the alert
          // fires for a token the user isn't currently viewing.
          const symbol = (market === 'sol')
            ? (msg.symbol || tokenSymbolMap?.[msg.mint] || (msg.mint?.slice(0, 4) || '?'))
            : (msg.symbol || '?')
          setNotifications(prev => [...prev, {
            id:        `notif_${Date.now()}_${msg.id}`,
            alertId:   msg.id,
            market,
            mint:      msg.mint,
            symbol,
            // Display the THRESHOLD the user set (what they saw at long-press),
            // not the live mcap of the triggering swap. The live value is a
            // single raw swap that can be an outlier/MEV print — confusing and
            // sometimes wildly off (e.g. 669M on a 22K token). Whatever field
            // the toast binds to ('mcap'), it now shows the armed price.
            mcap:      msg.threshold,
            threshold: msg.threshold,      // user's set threshold
            liveMcap:  msg.mcap,           // raw mcap at trigger, kept for reference
            direction: msg.direction,
            ts: Date.now(),
          }])
          // Audible bell — synthesized via Web Audio, no asset needed.
          playBell()
          // Browser notification (best-effort)
          try {
            if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
              new Notification('SolCheddar Alert', {
                body: `${symbol} crossed ${msg.direction} ${formatMcap(msg.threshold)}`,
                tag: msg.id,
              })
            }
          } catch {}
          break
        }
        case 'alert_error': {
          console.warn('[Alerts]', msg.msg)
          break
        }
        default: break
      }
    }
    ws.addEventListener('message', handler)
    return () => ws.removeEventListener('message', handler)
  }, [wsRef?.current, status, tokenSymbolMap]) // eslint-disable-line react-hooks/exhaustive-deps

  // ===== Wallet sync =====
  // Server pushes alerts_list automatically when set_wallet is received.
  // We just clear the local mirror if the wallet goes null/changes so a
  // stale list from the previous wallet isn't visible during the gap.
  useEffect(() => {
    if (currentWallet !== lastWalletSeenRef.current) {
      lastWalletSeenRef.current = currentWallet
      if (!currentWallet) setAlerts([])
    }
  }, [currentWallet])

  const refreshAlerts = useCallback(() => {
    const ws = wsRef?.current
    if (!ws || ws.readyState !== WebSocket.OPEN) return
    ws.send(JSON.stringify({ action: 'list_alerts' }))
  }, [wsRef])

  // ===== Long-press → set alert instantly =====
  // Hold 450ms without moving > tolerance → the alert is set at the press
  // row (direction inferred from press vs current price). Moving before the
  // timer fires cancels (it was a scroll/pan). Adjusting an existing alert
  // is now done by dragging its TAG (handled in the chart), not here.
  const pressRef = useRef(null) // { timer, startX, startY, mcap, market, mint, symbol, armed, moved, isTouch }
  const addAlertRef = useRef(null)
  const currentMcapRef = useRef(null)
  // Phone flow: long-press arms the crosshair (caller drives it from move),
  // and the alert commits on LIFT only if the finger never moved past
  // tolerance. Desktop keeps the original commit-on-timer behavior.
  const onLongPressArmRef = useRef(null)   // optional: caller notified when hold arms
  // Optional predicate from the caller: "is crosshair mode ALREADY active?"
  // Phone alert semantics changed — the FIRST long-press only ENTERS crosshair
  // (no alert on lift); a SUBSEQUENT long-press while already in crosshair is
  // what sets an alert. The hook can't know the chart's crosshair state on its
  // own, so the chart supplies this; when it returns true at arm time, the
  // press is a "second press" and its lift-without-move commits. When false
  // (or absent — desktop / standalone), the press is the entry and commit is
  // suppressed for that one press.
  const isCrosshairActiveRef = useRef(null)
  // Optional resolver for the commit price. NOTE the exported name:
  // FPCC already calls setCommitMcapResolver(...) with optional chaining and
  // the hook never implemented it — so that wiring has been a silent no-op
  // and alerts have always committed at the press point regardless. Matching
  // the caller's existing name makes the chart side correct with no edit.
  //
  // The LIVE crosshair price. Scrubbing the crosshair to the
  // level you want IS the aiming gesture on a phone, so a press that arms
  // while crosshair mode is already up must commit where the crosshair ENDED,
  // not where the finger first landed. Without this the hook could only ever
  // offer p.mcap — the pointer-DOWN price — which is the wrong number the
  // moment the user aims.
  const commitMcapResolverRef = useRef(null)

  const cancelPress = () => {
    const p = pressRef.current
    if (p?.timer) { clearTimeout(p.timer) }
    pressRef.current = null
  }

  const handlePointerDown = useCallback((e, mcapAtCursor, currentMcap) => {
    if (e.button !== 0 && e.pointerType !== 'touch') return
    if (!Number.isFinite(mcapAtCursor)) return
    cancelPress()
    if (Number.isFinite(currentMcap)) currentMcapRef.current = currentMcap
    const startX = e.clientX, startY = e.clientY
    const isTouch = e.pointerType === 'touch'
    const snapshotMarket = market
    const snapshotMint   = market === 'sol' ? currentToken : null
    const snapshotSymbol = market === 'sol'
      ? (tokenSymbolMap?.[currentToken] || null)
      : currentSymbol

    const commit = (mcap) => {
      const cur = currentMcapRef.current
      const direction = Number.isFinite(cur) ? (mcap >= cur ? 'above' : 'below') : 'above'
      const add = addAlertRef.current
      if (add && Number.isFinite(mcap)) {
        add({ market: snapshotMarket, mint: snapshotMint, symbol: snapshotSymbol, mcap, direction })
      }
    }

    const timer = setTimeout(() => {
      const p = pressRef.current
      if (!p) return
      if (isTouch) {
        // Phone: DON'T commit yet. Arm the crosshair; the alert fires on
        // lift-without-move (handlePointerUp) — but ONLY if crosshair mode was
        // already active BEFORE this press. Capture that state now, before the
        // arm callback flips it on, so the entry press (crosshair off → on)
        // sets no alert and only a subsequent press (already on) does.
        const alreadyInCrosshair = !!(isCrosshairActiveRef.current && isCrosshairActiveRef.current())
        p.armed = true
        p.suppressCommit = !alreadyInCrosshair   // entry press = no alert on lift
        if (onLongPressArmRef.current) onLongPressArmRef.current(p.mcap, startX, startY)
      } else {
        // Desktop: commit immediately on the held timer (unchanged behavior).
        pressRef.current = null
        commit(p.mcap)
      }
    }, LONG_PRESS_MS)

    pressRef.current = {
      timer, startX, startY, mcap: mcapAtCursor,
      market: snapshotMarket, mint: snapshotMint, symbol: snapshotSymbol,
      armed: false, moved: false, isTouch, commit, suppressCommit: false,
    }
  }, [currentToken, currentSymbol, market, tokenSymbolMap])

  const handlePointerMove = useCallback((e) => {
    const p = pressRef.current
    if (!p) return
    const dx = e.clientX - p.startX
    const dy = e.clientY - p.startY
    const movedPastTol = dx*dx + dy*dy > LONG_PRESS_TOLERANCE_PX * LONG_PRESS_TOLERANCE_PX
    if (!p.armed) {
      // Pre-arm: movement means it was a scroll/pan → cancel.
      if (movedPastTol) cancelPress()
    } else {
      // Armed (phone crosshair active): mark moved so lift won't set an alert.
      if (movedPastTol) p.moved = true
    }
  }, [])

  const handlePointerUp = useCallback(() => {
    const p = pressRef.current
    // Phone: an armed long-press commits on lift, unless it was the
    // crosshair-ENTRY press (suppressCommit), which only opens the mode.
    //
    // `moved` no longer blocks it. That check assumed a press was a one-shot
    // gesture where movement meant "scrolling, not aiming". Once crosshair
    // mode persists, movement IS aiming — you hold, scrub to the level, and
    // lift. Requiring stillness made a deliberately-placed alert impossible:
    // positioning the crosshair guaranteed moved=true and the lift did
    // nothing, which is exactly the "triggers but never registers" symptom.
    //
    // Price comes from the LIVE crosshair when one is supplied, falling back
    // to the pointer-down price for desktop and standalone callers.
    if (p && p.isTouch && p.armed && !p.suppressCommit) {
      const live = commitMcapResolverRef.current ? commitMcapResolverRef.current() : null
      p.commit(Number.isFinite(live) && live > 0 ? live : p.mcap)
    }
    cancelPress()
  }, [])
  const closeMenu = useCallback(() => setMenuState(null), [])

  // ===== Mutations (forward to server) =====
  // Accepts an options object: { market, mint, symbol, mcap, direction }.
  // For backward compat with the old (mint, mcap, direction) positional
  // call shape, the legacy form is auto-promoted to market='sol'.
  const addAlert = useCallback((optsOrMint, mcapPos, directionPos) => {
    let market, mint, symbol, mcap, direction
    if (optsOrMint && typeof optsOrMint === 'object') {
      ({ market = 'sol', mint = null, symbol = null, mcap, direction } = optsOrMint)
    } else {
      // Legacy: addAlert(mint, mcap, direction) → sol market
      market = 'sol'
      mint = optsOrMint
      symbol = null
      mcap = mcapPos
      direction = directionPos
    }
    if (!Number.isFinite(mcap)) return
    if (direction !== 'above' && direction !== 'below') return
    if (market === 'sol' && !mint) return
    if (market !== 'sol' && !symbol) return

    const ws = wsRef?.current
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      console.warn('[Alerts] ws not open, cannot add')
      return
    }
    if (!currentWallet) {
      console.warn('[Alerts] no wallet set, cannot add')
      return
    }
    if (dbgOn()) dbg('[AlertDebug] sending add_alert', { market, mint, symbol, mcap, direction, wallet: currentWallet })
    ws.send(JSON.stringify({
      action: 'add_alert',
      market, mint, symbol, mcap, direction,
    }))
    // Ask for permission once after first add
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      try { Notification.requestPermission() } catch {}
    }
  }, [wsRef, currentWallet])

  // Keep the long-press ref pointed at the latest addAlert.
  addAlertRef.current = addAlert

  const removeAlert = useCallback((id) => {
    if (!id) return
    const ws = wsRef?.current
    if (!ws || ws.readyState !== WebSocket.OPEN) return
    // Optimistic local removal so the UI is responsive; server confirms
    // (idempotent server-side, only matches alerts owned by this wallet).
    setAlerts(prev => prev.filter(a => a.id !== id))
    ws.send(JSON.stringify({ action: 'remove_alert', id }))
  }, [wsRef])

  // Move an existing alert to a new mcap. The server has no update action,
  // so this is remove-old + add-new. Direction is recomputed from the new
  // price vs the live current price (drag above → cross-above, below →
  // cross-below), matching how a fresh alert is set.
  const updateAlert = useCallback((alert, newMcap, currentMcap) => {
    if (!alert || !Number.isFinite(newMcap)) return
    const ws = wsRef?.current
    if (!ws || ws.readyState !== WebSocket.OPEN || !currentWallet) return
    const cur = Number.isFinite(currentMcap) ? currentMcap : currentMcapRef.current
    const direction = Number.isFinite(cur) ? (newMcap >= cur ? 'above' : 'below') : alert.direction
    // Optimistic: drop the old one locally; server echoes the add.
    setAlerts(prev => prev.filter(a => a.id !== alert.id))
    ws.send(JSON.stringify({ action: 'remove_alert', id: alert.id }))
    ws.send(JSON.stringify({
      action: 'add_alert',
      market: alert.market || 'sol',
      mint:   alert.mint || null,
      symbol: alert.symbol || null,
      mcap:   newMcap,
      direction,
    }))
  }, [wsRef, currentWallet])

  // ===== Notification UX =====
  const dismissNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(n => n.id !== id))
  }, [])

  const handleNotificationClick = useCallback((notif) => {
    // Only switch tokens for sol-market alerts. Futures toasts just dismiss
    // — no equivalent to "go to this token" since the user is already on
    // a futures chart for that symbol, or would need a separate router.
    if (notif.market === 'sol' && notif.mint && onSwitchToken && notif.mint !== currentToken) {
      onSwitchToken(notif.mint)
    }
    dismissNotification(notif.id)
  }, [onSwitchToken, currentToken, dismissNotification])

  // Auto-dismiss after 12s
  useEffect(() => {
    if (notifications.length === 0) return
    const timers = notifications.map(n => {
      const elapsed = Date.now() - n.ts
      const remaining = Math.max(0, 12000 - elapsed)
      return setTimeout(() => dismissNotification(n.id), remaining)
    })
    return () => timers.forEach(clearTimeout)
  }, [notifications, dismissNotification])

  const alertsForCurrentToken = useMemo(() => {
    if (market === 'sol') {
      return alerts.filter(a => (a.market === 'sol' || !a.market) && a.mint === currentToken)
    }
    return alerts.filter(a => a.market === market && a.symbol === currentSymbol)
  }, [alerts, currentToken, currentSymbol, market])

  return {
    alerts,
    alertsForCurrentToken,
    notifications,
    menuState,
    addAlert,
    removeAlert,
    updateAlert,
    refreshAlerts,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    // Chart registers a callback here to be told when a touch long-press has
    // armed (crosshair should start following the finger). fn(mcap, x, y).
    setOnLongPressArm: (fn) => { onLongPressArmRef.current = fn },
    // Chart registers a predicate here returning whether crosshair mode is
    // currently active, so the hook can tell an entry press (no alert) from a
    // subsequent in-crosshair press (sets an alert). fn() => boolean.
    setIsCrosshairActive: (fn) => { isCrosshairActiveRef.current = fn },
    setCommitMcapResolver: (fn) => { commitMcapResolverRef.current = fn },
    closeMenu,
    dismissNotification,
    handleNotificationClick,
    formatMcap,
  }
}